import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";
import type {
  CreateErrorPatternRequest,
  DeleteErrorPatternsRequest,
  UpdateErrorPatternRequest,
} from "@/types/collection-types";
import {
  createErrorPattern,
  deleteErrorPatterns,
  fetchErrorPatterns,
  updateErrorPattern,
} from "@/api/errorPatterns";

/**
 * 오류 패턴 목록 조회 훅
 */
export function useErrorPatternsQuery() {
  return useQuery({
    queryKey: queryKeys.errorPatterns.list(),
    queryFn: fetchErrorPatterns,
  });
}

/**
 * 오류 패턴 생성 훅
 */
export function useCreateErrorPatternMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateErrorPatternRequest) => createErrorPattern(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.errorPatterns.list() });
      toast({
        title: "패턴 생성 완료",
        description: "오류 패턴이 성공적으로 생성되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "패턴 생성 실패",
        description: error.message || "오류 패턴 생성에 실패했습니다.",
        variant: "destructive",
      });
    },
  });
}

/**
 * 오류 패턴 수정 훅
 */
export function useUpdateErrorPatternMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patternId, request }: { patternId: number; request: UpdateErrorPatternRequest }) =>
      updateErrorPattern(patternId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.errorPatterns.list() });
      toast({
        title: "패턴 수정 완료",
        description: "오류 패턴이 성공적으로 수정되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "패턴 수정 실패",
        description: error.message || "오류 패턴 수정에 실패했습니다.",
        variant: "destructive",
      });
    },
  });
}

/**
 * 오류 패턴 삭제 훅
 */
export function useDeleteErrorPatternsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DeleteErrorPatternsRequest) => deleteErrorPatterns(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.errorPatterns.list() });
      toast({
        title: "패턴 삭제 완료",
        description: `${data.deleted_count}개의 패턴이 삭제되었습니다.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "패턴 삭제 실패",
        description: error.message || "오류 패턴 삭제에 실패했습니다.",
        variant: "destructive",
      });
    },
  });
}
